import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { useAuthStore } from './stores/authStore'
import { initializeNotificationsStore } from './stores/notificationsStore'
import { Toaster } from './components/ui/toaster'
import PerformanceProvider from './components/PerformanceProvider'
import ProtectedRoute from './components/ProtectedRoute'
import { Login, Signup } from './pages/auth'
import OAuthCallback from './pages/auth/OAuthCallback'
import VerifyEmail from './pages/auth/VerifyEmail'
import VerificationSent from './pages/auth/VerificationSent'
import MagicLinkSent from './pages/auth/MagicLinkSent'
import WaitlistSignup from './pages/auth/WaitlistSignup'
import WaitlistConfirmation from './pages/auth/WaitlistConfirmation'
import WaitlistVerified from './pages/auth/WaitlistVerified'
import Dashboard from './pages/Dashboard'
import Chat from './pages/Chat'
import BankEntry from './pages/BankEntry'
import PurchaseEntry from './pages/PurchaseEntry'
import InvoiceEntry from './pages/InvoiceEntry'
import AuditLog from './pages/AuditLog'
import Reports from './pages/Reports'
import Settings from './pages/Settings'
import ExternalTools from './pages/ExternalTools'
import LogViewer from './pages/LogViewer'
import Homepage from './pages/Homepage'
import RunPreview from './pages/RunPreview'
import { checkAndClearIfNeeded } from './utils/clearStaleData'
import './App.css'


// Public Route Component (redirect to dashboard if authenticated)
interface PublicRouteProps {
  children: React.ReactNode
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <>{children}</>
}

function App() {
  const { checkAuth, isLoading, isAuthenticated } = useAuthStore()

  // Check authentication status and initialize stores on app initialization
  useEffect(() => {
    // Clear stale data on app startup (runs once)
    checkAndClearIfNeeded()
    
    checkAuth()
    
    // Initialize notifications store for authenticated users
    if (isAuthenticated) {
      initializeNotificationsStore()
    }
  }, [checkAuth, isAuthenticated])

  // Show loading spinner during initial auth check
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <PerformanceProvider enabledInProduction={false}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
          {/* Homepage - Public route for landing page */}
          <Route
            path="/"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Homepage />}
          />

          {/* Public routes (redirect to dashboard if authenticated) */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicRoute>
                <Signup />
              </PublicRoute>
            }
          />
          <Route
            path="/verify-email"
            element={
              <PublicRoute>
                <VerifyEmail />
              </PublicRoute>
            }
          />
          <Route
            path="/verification-sent"
            element={
              <PublicRoute>
                <VerificationSent />
              </PublicRoute>
            }
          />
          <Route
            path="/magic-link-sent"
            element={
              <PublicRoute>
                <MagicLinkSent />
              </PublicRoute>
            }
          />

          {/* Waitlist routes */}
          <Route
            path="/join-waitlist"
            element={
              <PublicRoute>
                <WaitlistSignup />
              </PublicRoute>
            }
          />
          <Route
            path="/waitlist-confirmation"
            element={
              <PublicRoute>
                <WaitlistConfirmation />
              </PublicRoute>
            }
          />
          <Route
            path="/waitlist-verified"
            element={
              <PublicRoute>
                <WaitlistVerified />
              </PublicRoute>
            }
          />

          {/* OAuth callback route (public) */}
          <Route
            path="/auth/callback"
            element={<OAuthCallback />}
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bank-entry"
            element={
              <ProtectedRoute>
                <BankEntry />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoice-entry"
            element={
              <ProtectedRoute>
                <InvoiceEntry />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit"
            element={
              <ProtectedRoute>
                <AuditLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/external-tools"
            element={
              <ProtectedRoute>
                <ExternalTools />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logs"
            element={
              <ProtectedRoute>
                <LogViewer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/preview/:runId"
            element={
              <ProtectedRoute>
                <RunPreview />
              </ProtectedRoute>
            }
          />

          {/* Catch all route - redirect to root */}
          <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            {/* Toast notifications */}
            <Toaster />
          </div>
        </Router>
      </AuthProvider>
    </PerformanceProvider>
  )
}

export default App
